import * as Sentry from "@sentry/react";
import { parse } from "cookie";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";

import { OpenAPI } from "./api";
import Jobs from "./components/candidate/all_matches/Jobs";
import EditProfile from "./components/candidate/edit_profile/EditProfile";
import OnboardingLinks from "./components/candidate/onboarding/OnboardingLinks";
import OnboardingPreferences from "./components/candidate/onboarding/OnboardingPreferences";
import OnboardingProfile from "./components/candidate/onboarding/OnboardingProfile";
import OnboardingSkills from "./components/candidate/onboarding/OnboardingSkills";
import Volunteer from "./components/candidate/Volunteer";
import YourMatches from "./components/candidate/your_matches/YourMatches";
// import AddCompany from "./components/recruiter/AddCompany";
import CandidateMatches from "./components/recruiter/CandidateMatches";
import JobPost from "./components/recruiter/JobPost";
import MyJobs from "./components/recruiter/my_jobs/MyJobs";
import RecPreferences from "./components/recruiter/RecPreferences";
import SkillPost from "./components/recruiter/SkillPost";
// import RecHome from "./components/recruiter/org_home/rec_home/RecHome";
import Login from "./constants/Login";
import RecSignup from "./constants/RecSignup";
import SbSignup from "./constants/SbSignup";
import Signup from "./constants/Signup";
import CandidateProfile from "./pages/CandidateProfile";
import CommissionerDashboard from "./pages/CommissionerDashboard";
import ExploreBrowse from "./pages/ExploreBrowse";
import FreeAgentHub from "./pages/FreeAgentHub";
import Home from "./pages/Home";
import JobShare from "./pages/JobShare";
import JudgingBoard from "./pages/JudgingBoard";
import LandingPage from "./pages/LandingPage";
import Leaderboard from "./pages/Leaderboard";
import NewHomePage from "./pages/NewHomePage";
import OrganizerHome from "./pages/OrganizerHome";
import OrgHome from "./pages/OrgHome";
import OrgProfile from "./pages/OrgProfile";
import Profile from "./pages/Profile";
import RecruitBoard from "./pages/RecruitBoard";
import ResourcesBrowse from "./pages/ResourcesBrowse";
import SubmissionPortal from "./pages/SubmissionPortal";
import ProtectedRoutes from "./routes/ProtectedRoutes";
import RequireOrganizerMode from "./routes/RequireOrganizerMode";
import SetProfileGuard from "./routes/SetProfileGuard";

OpenAPI.interceptors.request.use((request) => {
  const { csrftoken } = parse(document.cookie);
  if (request.headers && csrftoken) {
    request.headers["X-CSRFTOKEN"] = csrftoken;
  }
  return request;
});

const App = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex flex-col items-center justify-center h-screen">
          <p className="text-4xl text-gray-400">Oops!</p>
          <p className="text-2xl text-gray-400">Something went wrong.</p>
        </div>
      }
    >
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route element={<NewHomePage />} path="/" />
          <Route element={<NewHomePage />} path="/newhomepage" />
          <Route element={<LandingPage />} path="/landingpage" />
          <Route element={<Signup />} path="/signup" />
          <Route element={<RecSignup />} path="/recsignup" />
          <Route element={<SbSignup />} path="/sbsignup" />
          <Route element={<Login />} path="/login" />
          <Route element={<JobShare />} path="/job/:slug/:id" />

          {/* Candidate Routes */}
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <OnboardingSkills />
              </ProtectedRoutes>
            }
            path="/setskills"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <OnboardingLinks />
              </ProtectedRoutes>
            }
            path="/setworklinks"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <SetProfileGuard>
                  <OnboardingProfile />
                </SetProfileGuard>
              </ProtectedRoutes>
            }
            path="/setprofile"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <OnboardingPreferences />
              </ProtectedRoutes>
            }
            path="/setpreferences"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <Home />
              </ProtectedRoutes>
            }
            path="/home"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <Leaderboard />
              </ProtectedRoutes>
            }
            path="/leaderboard"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <RequireOrganizerMode>
                  <OrganizerHome />
                </RequireOrganizerMode>
              </ProtectedRoutes>
            }
            path="/organizer"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <RequireOrganizerMode>
                  <JudgingBoard />
                </RequireOrganizerMode>
              </ProtectedRoutes>
            }
            path="/judging"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <RequireOrganizerMode>
                  <CommissionerDashboard />
                </RequireOrganizerMode>
              </ProtectedRoutes>
            }
            path="/commissioner"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <FreeAgentHub />
              </ProtectedRoutes>
            }
            path="/hub"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <SubmissionPortal />
              </ProtectedRoutes>
            }
            path="/submissions"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <RecruitBoard />
              </ProtectedRoutes>
            }
            path="/recruit"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <ResourcesBrowse />
              </ProtectedRoutes>
            }
            path="/resources"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <ExploreBrowse />
              </ProtectedRoutes>
            }
            path="/explore"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <Profile />
              </ProtectedRoutes>
            }
            path="/profile"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <Volunteer />
              </ProtectedRoutes>
            }
            path="/volunteer"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <EditProfile />
              </ProtectedRoutes>
            }
            path="/editprofile"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <Jobs />
              </ProtectedRoutes>
            }
            path="/jobs"
          />
          <Route
            element={
              <ProtectedRoutes allowedRoles={["candidate"]}>
                <YourMatches />
              </ProtectedRoutes>
            }
            path="/yourmatches"
          />

          {/* Recruiter Routes */}
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <RecPreferences />
              </ProtectedRoutes>
            }
            path="/recpreferences"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <OrgHome />
              </ProtectedRoutes>
            }
            path="/orghome"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <CandidateMatches />
              </ProtectedRoutes>
            }
            path="/matches"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <JobPost />
              </ProtectedRoutes>
            }
            path="/jobpost"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <JobPost />
              </ProtectedRoutes>
            }
            path="/jobpost/:id"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <SkillPost />
              </ProtectedRoutes>
            }
            path="/skillpost/:id"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <MyJobs />
              </ProtectedRoutes>
            }
            path="/myjobs"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <OrgProfile />
              </ProtectedRoutes>
            }
            path="/orgprofile"
          />
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={["recruiter", "smallbiz_owner", "nonprofit"]}
              >
                <CandidateProfile />
              </ProtectedRoutes>
            }
            path="/candidate/:candidateId"
          />
        </Routes>
      </Router>
    </Sentry.ErrorBoundary>
  );
};

export default App;
